import React, { useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  Users,
  Calendar as CalendarIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Reservation } from "@/models/storeModel";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  parseISO,
  isSameMonth,
  isToday,
  addMonths,
  subMonths,
} from "date-fns";
import { cn } from "@/lib/currencyFormatter";
import { useTranslation } from "react-i18next";

interface CustomCalendarViewProps {
  reservations: Reservation[];
  onReservationSelect: (reservation: Reservation) => void;
  onDateSelect: (date: Date) => void;
  selectedDate?: Date;
}

export function CustomCalendarView({
  reservations,
  onReservationSelect,
  onDateSelect,
  selectedDate = new Date(),
}: CustomCalendarViewProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const { t } = useTranslation();

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const getReservationsForDate = (date: Date) => {
    return reservations.filter((reservation) =>
      isSameDay(parseISO(reservation.reservation_date), date)
    );
  };

  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));

  const getStatusColor = (status: string) => {
    switch (status) {
      case "confirmed":
        return "bg-green-500";
      case "cancelled":
        return "bg-red-500";
      case "completed":
        return "bg-blue-500";
      default:
        return "bg-gray-500";
    }
  };

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div className="h-full flex flex-col bg-white rounded-lg shadow-sm border">
      {/* Calendar Header */}
      <div className="flex items-center justify-between p-6 border-b">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {format(currentMonth, "MMMM yyyy")}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {t("calendar.viewAllUpcomingReservations")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={prevMonth}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={nextMonth}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Days of Week Header */}
      <div className="grid grid-cols-7 border-b">
        {dayNames.map((day) => (
          <div
            key={day}
            className="p-4 text-center text-sm font-medium text-gray-500 border-r last:border-r-0"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="flex-1 grid grid-cols-7 auto-rows-fr">
        {daysInMonth.map((date, index) => {
          const dayReservations = getReservationsForDate(date);
          const isSelected = selectedDate && isSameDay(date, selectedDate);
          const isCurrentMonth = isSameMonth(date, currentMonth);
          const isTodayDate = isToday(date);

          return (
            <div
              key={date.toISOString()}
              className={cn(
                "relative border-r border-b last:border-r-0 p-2 cursor-pointer transition-colors min-h-[120px] rounded-xl",
                !isCurrentMonth &&
                  "bg-gradient-to-br from-gray-50/50 to-gray-100/50",
                isSelected &&
                  "bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-200",
                isTodayDate &&
                  "bg-gradient-to-br from-blue-50/30 to-blue-100/30",
                !isSelected &&
                  !isTodayDate &&
                  isCurrentMonth &&
                  "hover:bg-gradient-to-br hover:from-gray-50 hover:to-gray-100"
              )}
              onClick={() => onDateSelect(date)}
            >
              {/* Date Number */}
              <div className="flex items-center justify-between mb-2">
                <span
                  className={cn(
                    "text-sm font-medium",
                    !isCurrentMonth && "text-gray-400",
                    isTodayDate && "text-blue-600 font-bold",
                    isSelected && "text-blue-600"
                  )}
                >
                  {format(date, "d")}
                </span>
                {dayReservations.length > 0 && (
                  <Badge variant="secondary" className="text-xs px-1 py-0">
                    {dayReservations.length}
                  </Badge>
                )}
              </div>

              {/* Reservations for this day */}
              <div className="space-y-1">
                {dayReservations.slice(0, 2).map((reservation) => (
                  <div
                    key={reservation.id}
                    className={cn(
                      "p-1.5 rounded-lg text-xs cursor-pointer transition-all hover:shadow-sm",
                      "bg-white border border-gray-200 hover:border-gray-300"
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      onReservationSelect(reservation);
                    }}
                  >
                    <div className="flex items-center gap-1 mb-1">
                      <div
                        className={cn(
                          "w-2 h-2 rounded-full",
                          getStatusColor(reservation.status)
                        )}
                      />
                      <span className="font-medium truncate flex-1">
                        {reservation.customer_name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        <span>{reservation.reservation_time}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        <span>{reservation.party_size}</span>
                      </div>
                    </div>
                  </div>
                ))}

                {dayReservations.length > 2 && (
                  <div className="text-xs text-gray-500 text-center py-1">
                    {t("calendar.moreReservations", {
                      count: dayReservations.length - 2,
                    })}
                  </div>
                )}
              </div>

              {/* Today indicator */}
              {isTodayDate && (
                <div className="absolute top-1 right-1">
                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="p-4 border-t bg-gray-50/50">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span>{t("calendar.confirmed")}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <span>{t("calendar.completed")}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span>{t("calendar.cancelled")}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <CalendarIcon className="h-4 w-4" />
            <span>
              {t("calendar.totalReservations", { count: reservations.length })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
